"use client";

import { useMediaQuery } from "@/hooks/use-media-query";

/**
 * Hauteur (px) au-dessous de laquelle la chrome se comprime.
 *
 * 480 px sépare proprement deux mondes : en dessous il n'y a que des
 * téléphones couchés — de l'iPhone SE (375) au 16 Pro Max (440) — et au-dessus
 * commencent les tablettes en paysage (768) et tous les portables (≥ 600).
 * Le seuil ne dépend pas de la largeur : un grand téléphone en paysage
 * dépasse 768 px de large et manquerait un critère basé sur le palier.
 */
export const SHORT_VIEWPORT_MAX_HEIGHT = 480;

const SHORT_VIEWPORT_QUERY = `(max-height: ${SHORT_VIEWPORT_MAX_HEIGHT}px)`;

/**
 * Vrai quand l'écran est trop bas pour porter la chrome à sa taille normale.
 *
 * Le pendant CSS s'écrit `[@media(max-height:480px)]:` — c'est lui qui pilote
 * les hauteurs de barres, parce qu'un basculement peint par le CSS est juste
 * dès la première frame là où ce hook, comme {@link useMediaQuery}, répond
 * `false` au premier rendu. Ne l'utiliser que pour des décisions qui ne se
 * voient pas : monter ou non un panneau, brancher ou non un raccourci.
 *
 * Les deux écritures doivent bouger ensemble : Tailwind ne sait pas lire la
 * constante ci-dessus.
 */
export function useShortViewport(): boolean {
  return useMediaQuery(SHORT_VIEWPORT_QUERY);
}

/*
 * Il y avait ici `matchesShortViewport()`, la même question posée à l'instant
 * plutôt qu'observée, pour servir de valeur **initiale**. Ses deux appelants
 * étaient les vignettes caméra, et c'est l'usage lui-même qui était faux : une
 * valeur retenue au montage décrit l'orientation d'entrée, et sur un téléphone
 * on entre toujours par le portrait. Les deux sont repassés au hook (voir
 * `collapsedByDefault` dans `video-widget.tsx`), et plus rien ne l'appelait.
 */

/**
 * Largeur (rem) au-dessus de laquelle un appareil debout n'est plus un
 * téléphone. C'est `BREAKPOINTS.medium`, donc le `md:` de Tailwind : un iPad
 * debout fait 768 px et reste au-dessus. Les deux doivent bouger ensemble.
 */
const HANDHELD_PORTRAIT_MAX_WIDTH_REM = 48;

/**
 * « Un téléphone », dans les deux orientations — la condition que lit tout ce
 * qui réorganise le studio pour un petit écran.
 *
 * **Deux requêtes, pas une**, parce qu'aucune dimension seule ne reconnaît
 * l'appareil : couché, un téléphone dépasse `md` en largeur et passerait pour
 * une tablette, c'est sa hauteur qui le trahit ; debout, c'est exactement
 * l'inverse. Elles sont évaluées en « ou » par {@link useHandheld}, ce qui
 * évite de dépendre de l'imbrication de conditions des Media Queries 4.
 *
 * **`pointer: coarse` n'est décoratif dans aucune des deux.** `orientation` et
 * `max-height` décrivent le viewport, pas le matériel : une fenêtre de bureau
 * qu'on a étroitie ou écrasée y répond « oui ». Comprimer des barres chez elle
 * est sans conséquence — c'est ce que fait {@link SHORT_VIEWPORT_MAX_HEIGHT}
 * tout seul — mais lui retirer des commandes serait une perte sèche.
 *
 * Le pendant CSS est la variante `handheld:`, définie une fois dans
 * `app/globals.css` ; la vue Pixi en tient une troisième copie
 * (`lib/studio/view/witness-keyboard.ts`), étant sans React et sans CSS. Un
 * test compare les trois.
 */
export const HANDHELD_QUERIES: readonly string[] = [
  `(pointer: coarse) and (max-height: ${SHORT_VIEWPORT_MAX_HEIGHT}px)`,
  `(pointer: coarse) and (orientation: portrait) and (not (min-width: ${HANDHELD_PORTRAIT_MAX_WIDTH_REM}rem))`,
];

/**
 * Vrai sur un téléphone, couché ou debout.
 *
 * Comme {@link useShortViewport}, répond `false` au premier rendu : le bureau
 * ne voit donc jamais passer la version téléphone, ce qui est le bon sens de
 * l'erreur.
 */
export function useHandheld(): boolean {
  // Les deux hooks sont appelés à chaque rendu, jamais l'un à la place de
  // l'autre : `||` court-circuiterait l'appel, ce qu'un hook n'admet pas.
  const landscape = useMediaQuery(HANDHELD_QUERIES[0]);
  const portrait = useMediaQuery(HANDHELD_QUERIES[1]);

  return landscape || portrait;
}

/**
 * Vrai sur un téléphone **debout**, où la place manque en largeur et non en
 * hauteur — l'inverse exact du couché. Les caméras s'y posent en haut plutôt
 * qu'en bas, parce que c'est en bas que tombent les notes.
 */
export function useHandheldPortrait(): boolean {
  return useMediaQuery(HANDHELD_QUERIES[1]);
}
